import { useEffect, useRef, useState } from 'react';
import { Application } from 'pixi.js';
import { Box, Typography, Paper, Fade, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeckRenderer from '../game/DeckRenderer';
import GameStart from '../game/GameStart';
import Deck from '../game/Deck';
import useIsMobileDevice from '../hooks/useIsMobileDevice';
import { GAME_TIMINGS } from '../config/gameTimings';
import { ANIMATION_DURATIONS } from '../config/animationConfig.js';

/**
 * GameStartSequence Component
 * 
 * Displays the initial game start sequence where:
 * 1. A deck of cards is shown stacked
 * 2. A random player cuts the deck
 * 3. A card is revealed
 * 4. The starting dealer is determined based on the card suit
 * 
 * This should only be shown when both teams have 0 points (start of the game)
 */
const GameStartSequence = ({ players, onComplete }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobileDevice();
    const canvasRef = useRef(null);
    const appRef = useRef(null);
    const deckRendererRef = useRef(null);

    const [stage, setStage] = useState('initializing'); // initializing, showing, revealing, result, complete
    const [result, setResult] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        let app;
        let mounted = true;

        const initializeSequence = async () => {
            try {
                console.log('🎬 Starting GameStartSequence initialization...');

                // Create PixiJS application
                app = new Application();
                console.log('✅ PixiJS Application created');

                // Adjust canvas size to accommodate side-by-side layout (deck on left, revealed card on right)
                const canvasWidth = isMobile
                    ? Math.min(window.innerWidth * 0.9, 600)  // Wider canvas for mobile
                    : Math.min(window.innerWidth * 0.5, 500);  // Wider canvas for desktop too
                const canvasHeight = isMobile
                    ? Math.min(window.innerHeight * 0.5, 280)  // Shorter height for mobile landscape
                    : Math.min(window.innerHeight * 0.6, 300);  // Adjusted height for desktop

                console.log(`📐 Canvas size: ${canvasWidth}x${canvasHeight}`);

                await app.init({
                    width: canvasWidth,
                    height: canvasHeight,
                    backgroundColor: 0x1a1a2e,
                    resolution: window.devicePixelRatio || 1,
                    autoDensity: true,
                });
                console.log('✅ PixiJS app initialized');

                if (!mounted) {
                    console.log('⚠️ Component unmounted, aborting');
                    app.destroy(true, true);
                    return;
                }

                appRef.current = app;

                if (canvasRef.current) {
                    canvasRef.current.appendChild(app.canvas);
                    console.log('✅ Canvas appended to DOM');
                }

                // Create and shuffle deck
                console.log('🃏 Creating deck...');
                const deck = new Deck();
                deck.shuffle();
                console.log(`✅ Deck created with ${deck.cards.length} cards`);

                // Create deck renderer
                console.log('🎨 Creating DeckRenderer...');
                const deckRenderer = new DeckRenderer(app, deck);
                deckRendererRef.current = deckRenderer;

                // Apply responsive card sizing
                const screenWidth = app.screen.width;
                const screenHeight = app.screen.height;

                let cardWidth, cardHeight;
                if (isMobile) {
                    // Use 18% of screen width for mobile devices
                    cardWidth = Math.floor(screenWidth * 0.18);
                    cardHeight = Math.floor(cardWidth * 1.5);
                } else {
                    // Smaller cards for desktop
                    cardWidth = Math.floor(screenWidth * 0.15);
                    cardHeight = Math.floor(cardWidth * 1.5);
                }

                console.log(`🃏 Card size: ${cardWidth}x${cardHeight}`);
                deckRenderer.setCardSize(cardWidth, cardHeight);

                // Position deck on the left side to extract to the right
                // Card width + margin to ensure the deck doesn't get cut off
                const leftMargin = cardWidth * 0.8;
                deckRenderer.setPosition(leftMargin + cardWidth / 2, screenHeight * 0.5);
                console.log(`📍 Deck position: (${leftMargin + cardWidth / 2}, ${screenHeight * 0.5})`);

                // Render the deck
                console.log('🎨 Rendering deck...');
                await deckRenderer.render();
                console.log('✅ Deck rendered successfully');

                setStage('showing');

                // Wait a moment before starting the sequence
                console.log(`⏳ Waiting ${ANIMATION_DURATIONS.SEQUENCE_WAIT}ms before reveal...`);
                await new Promise(resolve => setTimeout(resolve, ANIMATION_DURATIONS.SEQUENCE_WAIT));

                if (!mounted) {
                    console.log('⚠️ Component unmounted, aborting reveal');
                    return;
                }

                // Start the reveal sequence automatically
                console.log('🎬 Starting reveal sequence...');
                await startRevealSequence(deck, deckRenderer);

            } catch (error) {
                console.error('❌ Error initializing GameStartSequence:', error);
                console.error('Error name:', error?.name);
                console.error('Error message:', error?.message);
                console.error('Error stack:', error?.stack);
            }
        };

        const startRevealSequence = async (deck, deckRenderer) => {
            if (!mounted) return;

            setIsAnimating(true);
            setStage('revealing');

            try {
                // Randomly select a player to cut the deck
                const cuttingPlayerIndex = Math.floor(Math.random() * 4);

                // Select a random card from the deck
                const randomCardIndex = Math.floor(Math.random() * deck.cards.length);

                // Extract and reveal the card
                // Always extract horizontally to the right
                const screenWidth = appRef.current?.screen.width || window.innerWidth;

                // Calculate extract distance to maintain equal margins on both sides
                // Formula: screenWidth - (2 * leftMargin) - cardWidth (space between cards)
                const cardWidth = isMobile
                    ? Math.floor(screenWidth * 0.18)
                    : Math.floor(screenWidth * 0.15);
                const leftMargin = cardWidth * 0.8;
                // Distance to move the card to have equal margins on both sides
                const extractDistance = screenWidth - (2 * (leftMargin + cardWidth / 2)) - cardWidth;
                const extractDirection = 'right';
                const { card, sprite } = await deckRenderer.extractAndRevealCard(randomCardIndex, extractDistance, extractDirection);

                if (!mounted) return;

                // Determine the starting dealer
                const gameStart = new GameStart(players);
                const dealerIndex = gameStart.getDealerIndex(cuttingPlayerIndex, card.suit);
                const position = gameStart.getPositionDescription(cuttingPlayerIndex, dealerIndex);

                const sequenceResult = {
                    cuttingPlayer: players[cuttingPlayerIndex],
                    revealedCard: card,
                    startingDealer: players[dealerIndex],
                    dealerIndex: dealerIndex,
                    position: position
                };

                setResult(sequenceResult);
                setStage('result');

            } catch (error) {
                console.error('Error during reveal sequence:', error);
            } finally {
                setIsAnimating(false);
            }
        };

        initializeSequence();

        return () => {
            mounted = false;
            if (deckRendererRef.current) {
                deckRendererRef.current.destroy();
                deckRendererRef.current = null;
            }
            if (appRef.current) {
                appRef.current.destroy(true, true);
                appRef.current = null;
            }
        };
    }, [players]);

    const handleContinue = () => {
        setStage('complete');
        if (onComplete) {
            onComplete(result);
        }
    };

    // Helper to get suit name in Spanish
    const getSuitName = (suit) => {
        const suitNames = {
            'oros': 'Oros',
            'copas': 'Copas',
            'espadas': 'Espadas',
            'bastos': 'Bastos'
        };
        return suitNames[suit] || suit;
    };

    // Helper to get position description in Spanish
    const getPositionText = (position) => {
        const positions = {
            'same': 'el mismo jugador',
            'right': 'el jugador a su derecha',
            'opposite': 'el jugador de enfrente',
            'left': 'el jugador a su izquierda'
        };
        return positions[position] || position;
    };

    if (stage === 'complete') {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 2, sm: 4 },
                p: 2
            }}
        >
            {/* Canvas Container - Left side in landscape */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    flex: { xs: 'none', sm: '0 0 auto' }
                }}
            >
                {/* Title - moved inside canvas container */}
                <Fade in={stage !== 'initializing'} timeout={800}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            textAlign: 'center'
                        }}
                    >
                        {stage === 'showing' && '¿Quién reparte primero?'}
                        {stage === 'revealing' && 'Oros, copas, espadas y bastos...'}
                        {stage === 'result' && '¡Ya sabemos quien reparte!'}
                    </Typography>
                </Fade>

                <Box
                    ref={canvasRef}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                />
            </Box>

            {/* Result Panel - Right side in landscape */}
            {stage === 'result' && result && (
                <Fade in={true} timeout={800}>
                    <Paper
                        elevation={10}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            maxWidth: { xs: '90%', sm: 450 },
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: 3,
                            textAlign: 'center',
                            flex: { xs: 'none', sm: '0 0 auto' }
                        }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {result.cuttingPlayer.avatar} {result.cuttingPlayer.name} cortó el mazo
                        </Typography>

                        <Typography variant="body1" sx={{ my: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Carta revelada: <strong>{result.revealedCard.value} de {getSuitName(result.revealedCard.suit)}</strong>
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                my: 2,
                                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                borderRadius: 2,
                                border: '2px solid #667eea'
                            }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                                {result.startingDealer.avatar} {result.startingDealer.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Será el primer repartidor
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({getPositionText(result.position)})
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleContinue}
                            sx={{
                                mt: 2,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                }
                            }}
                        >
                            Continuar al juego
                        </Button>
                    </Paper>
                </Fade>
            )}

            {/* Loading indicator */}
            {(stage === 'initializing' || stage === 'showing') && (
                <Fade in={true}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Preparando el mazo...
                    </Typography>
                </Fade>
            )}
        </Box>
    );
};

export default GameStartSequence;
